import React, { useState } from "react";
import { Box, IconButton, Typography, Button, TextField } from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Localized } from "@fluent/react";
import { ServerErrorMsg } from "../../../app/ServerErrorMsg";
import { APIError } from "../../../app/appTypes";
import { If } from "../../../app/If";

export interface DocumentSectionHeaderProps {
    numeration: string | number;
    name: string;
    isEditable: boolean;
    error: APIError | null;
    onSaveName: (newName: string) => Promise<void> | void;
    onDeleteClick: () => void;
}

export const DocumentSectionHeader: React.FC<DocumentSectionHeaderProps> = ({
    numeration,
    name,
    isEditable,
    error,
    onSaveName,
    onDeleteClick,
}) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [tempTitle, setTempTitle] = useState(name);
    const [isHovered, setIsHovered] = useState(false);

    const handleTitleClick = () => {
        if (!isEditable) return;
        setIsEditingTitle(true);
        setTempTitle(name);
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTempTitle(event.target.value);
    };

    const handleApprove = async () => {
        if (!tempTitle?.trim()) return;
        try {
            await onSaveName(tempTitle.trim());
            setIsEditingTitle(false);
        } catch {
        }
    };

    const handleCancel = () => {
        setIsEditingTitle(false);
        setTempTitle(name);
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={1}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <ServerErrorMsg err={error} />

            <Typography variant="h5" color="primary">
                {numeration}.
            </Typography>

            {isEditingTitle ? (
                <>
                    <TextField
                        value={tempTitle}
                        onChange={handleTitleChange}
                        autoFocus
                        variant="standard"
                        fullWidth
                    />
                    <IconButton color="success" onClick={handleApprove}>
                        <CheckIcon />
                    </IconButton>
                    <IconButton color="error" onClick={handleCancel}>
                        <CloseIcon />
                    </IconButton>
                </>
            ) : (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ cursor: isEditable ? "pointer" : "default" }}
                    onClick={handleTitleClick}
                >
                    <Typography
                        variant="h5"
                        color="primary"
                        sx={{
                            transition: "0.2s",
                            ...(isHovered &&
                                isEditable && {
                                textDecoration: "underline",
                                color: "secondary.main",
                            }),
                        }}
                    >
                        {name}
                    </Typography>
                    {isHovered && isEditable && (
                        <IconButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            )}

            <If condition={isEditable}>
                <Button
                    disabled={!isEditable}
                    onClick={onDeleteClick}
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    sx={{ ml: "auto" }}
                    size="small"
                >
                    <Localized id="delete">Usuń</Localized>
                </Button>
            </If>
        </Box>
    );
};
